import { useTranslation } from 'react-i18next';
import { cn, formatDate } from 'lib/utils';
import { AI_CREDITS_METER } from 'features/credits/constants/credits';
import type { HeldPlace } from 'features/subscription/services/seat-service';
import type { PlanSeats } from 'features/subscription/utils/seat-pools';
import { isIncompleteCheckout } from 'features/subscription/utils/resolve-plan-select-action';
import { AccountRow } from './account-row';
import {
  accountBadgeClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';

interface YourPlanCardProps {
  place?: HeldPlace;
  planSeats: PlanSeats[];
  isAdmin: boolean;
}

/** The seat the signed-in person holds: its plan, when it renews, and the credits it gives a month. */
export function YourPlanCard({ place, planSeats, isAdmin }: YourPlanCardProps) {
  const { t } = useTranslation();

  if (!place) {
    return (
      <section className={cn(accountSectionClassName, 'px-6 pb-2 pt-5')}>
        <h2 className={accountSectionTitleClassName}>{t('YOUR_PLAN', 'Your plan')}</h2>
        <AccountRow
          title={t('NO_PLAN', 'No plan')}
          hint={
            isAdmin
              ? t('NO_SEAT_ADMIN_HINT', 'You hold no seat. Give yourself a plan from the members page.')
              : t('NO_SEAT_MEMBER_HINT', 'You hold no seat. Ask your admin for a plan.')
          }
          isLast
        >
          <span
            className={cn(
              accountBadgeClassName,
              'sm:ml-auto bg-grey-100 text-grey-600 dark:bg-muted dark:text-muted-foreground'
            )}
          >
            {t('INACTIVE', 'Inactive')}
          </span>
        </AccountRow>
      </section>
    );
  }

  const isPending = isIncompleteCheckout(place.status);
  const plan = planSeats.find((entry) => entry.code === place.planCode)?.plan;
  const monthlyCredits = plan?.meters.find((meter) => meter.meterKey === AI_CREDITS_METER)?.includedQuantity;

  const details: string[] = [];
  if (place.cancelAtPeriodEnd) {
    details.push(t('ENDS_ON_DATE', { date: formatDate(place.currentPeriodEndUtc), defaultValue: 'Ends on {{date}}' }));
  } else if (!isPending) {
    details.push(t('RENEWS_ON_DATE', { date: formatDate(place.currentPeriodEndUtc), defaultValue: 'Renews on {{date}}' }));
  }
  if (monthlyCredits != null) {
    details.push(
      t('CREDITS_A_MONTH_COUNT', { credits: monthlyCredits.toLocaleString(), defaultValue: '{{credits}} credits a month' })
    );
  }

  return (
    <section className={cn(accountSectionClassName, 'px-6 pb-2 pt-5')}>
      <h2 className={accountSectionTitleClassName}>{t('YOUR_PLAN', 'Your plan')}</h2>
      <AccountRow title={place.planName || place.planCode} hint={details.join(' · ')} isLast>
        <span
          className={cn(
            accountBadgeClassName,
            'sm:ml-auto',
            isPending
              ? 'bg-warning-background text-warning-high-emphasis'
              : 'bg-success-50 text-success-700 dark:bg-success-700/20 dark:text-success-50'
          )}
        >
          {isPending ? t('PAYMENT_PENDING', 'Payment pending') : t('ACTIVE', 'Active')}
        </span>
      </AccountRow>
    </section>
  );
}
